import { memo, useCallback } from 'react';
import { MenuItem, Tooltip, useDropdownMenu } from '@aimsos/ui';

/**
 * One row of a `Dropdown` menu: a `MenuItem` that closes the menu after it runs. A disabled
 * action explains itself: the reason shows in a `Tooltip` and as the item's sub-text.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {() => void} [props.onSelect]
 * @param {import('lucide-react').LucideIcon} [props.icon]
 * @param {'medium'|'small'} [props.size='medium'] `small`: 16px icon and 12px text, for compact menus.
 * @param {string} [props.className] Extra classes on the row (e.g. a narrower minimum width).
 * @param {boolean} [props.disabled]
 * @param {string} [props.disabledReason]
 */
function MenuAction({ label, onSelect, icon, size = 'medium', className, disabled = false, disabledReason }) {
  const menu = useDropdownMenu();
  const handleClick = useCallback(() => {
    onSelect?.();
    menu?.close?.();
  }, [onSelect, menu]);

  const item = (
    <MenuItem
      label={label}
      size={size}
      className={className}
      icon={icon ?? false}
      disabled={disabled}
      description={disabled ? disabledReason : undefined}
      subtextMenu={disabled && Boolean(disabledReason)}
      onClick={handleClick}
    />
  );

  if (!disabled || !disabledReason) return item;

  return <Tooltip content={disabledReason}>{item}</Tooltip>;
}

export default memo(MenuAction);
